import { notification, Table, Space, Button } from 'antd';
import { DeleteOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import AddFilesModal from './components/AddFilesModal';
import MarkdownModal from './components/MarkdownModal';
import useTemplateState from '../../atoms/variables';

const UploadDocumentPage = ({
  id,
  type = 'asset',
  getCall = async () => {
    return [];
  },
  uploadCall = async () => {},
  deleteCall = async () => {},
}) => {
  const { t } = useTranslation();
  const { setPendingApiCalls } = useTemplateState();

  const { instance } = useMsal();
  const [files, setFiles] = useState([]);
  const [notificationApi, contextHolder] = notification.useNotification();
  const [markdownModalOpen, setMarkdownModalOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [markdownContent, setMarkdownContent] = useState('');

  useEffect(() => {
    triggerApiCalls();
  }, []);

  const groupedFiles = useMemo(() => {
    const map = new Map();

    files
      .filter((f) => !f.name.includes('.pdf/images/') && !f.name.endsWith('.md'))
      .forEach((f) => {
        const key = f?.metadata?.tree?.trim() || '';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(f);
      });

    return Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }),
    );
  }, [files, t]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (files.length > 0) {
        const filesInProcessing = files.filter((file) => file?.metadata?.status === '0');
        if (filesInProcessing.length > 0) {
          triggerApiCalls();
        }
      }
    }, 120000);

    return () => clearInterval(interval);
  }, [files]);

  const triggerApiCalls = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['getAssetFiles']));
      const response = await getCall(instance, type, id, false);
      setFiles(response);
    } catch (err) {
      console.log(err);
      notificationApi.error({
        message: t('errorLoad'),
        description: err?.response?.data?.message
          ? `${err?.response?.data?.message} (${err?.response?.status})`
          : err?.message,
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'getAssetFiles'));
    }
  };

  const deleteItem = async ({ file }) => {
    try {
      setPendingApiCalls((current) => current.concat(['deleteFile']));
      await deleteCall(instance, type, id, file?.name);
    } catch (err) {
      console.log(err);
      notificationApi.error({
        message: t('errorDelete'),
        description: err?.response?.data?.message
          ? `${err?.response?.data?.message} (${err?.response?.status})`
          : err?.message,
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'deleteFile'));
      triggerApiCalls();
    }
  };

  const showMarkdownModal = async (fileName) => {
    setSelectedFileName(fileName);
    
    const mdFileName = fileName.replace(/\.[^/.]+$/, '.md');
    const mdFile = files.find(f => f.name.endsWith(mdFileName));
    
    if (mdFile && mdFile.url) {
      try {
        const response = await fetch(mdFile.url);
        const content = await response.text();
        setMarkdownContent(content);
      } catch (error) {
        console.error('Error fetching markdown content:', error);
        setMarkdownContent('# Error\n\nCould not load markdown content.');
      }
    } else {
      setMarkdownContent('# No Content\n\nNo markdown file found for this document.');
    }
    
    setMarkdownModalOpen(true);
  };

  const downloadMdFile = (fileName) => {
    const mdFileName = fileName.replace(/\.[^/.]+$/, '.md');
    const mdFile = files.find(f => f.name.endsWith(mdFileName));
    if (mdFile && mdFile.url) {
      const link = document.createElement('a');
      link.href = mdFile.url;
      link.download = mdFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const hasDocumentInfo = (fileName) => {
    const file = files.find(f => f.name.endsWith(fileName));
    return file?.metadata?.document_info ? true : false;
  };

  console.log(files, 'files');

  return (
    <div className='UploadDocumentPage'>
      {contextHolder}

      <div className='manageFilesButtonsContainer' style={{ marginBottom: '16px' }}>
        <Space>
          
          <Button
            icon={<ReloadOutlined />}
            onClick={triggerApiCalls}
            type="default"
          >
            Refresh
          </Button>
          <AddFilesModal
            id={id}
            type={type}
            uploadCall={uploadCall}
            reloadFileList={triggerApiCalls}
          />
        </Space>
      </div>

      <div>
        {console.log(groupedFiles)}
        {groupedFiles.map(([groupName, docs]) => {
          const columns = [
            {
              title: t('fileName'),
              dataIndex: 'name',
              key: 'name',
              render: (_, record) => (
                <a 
                  href={record.url} 
                  target='_blank' 
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                >
                  {record?.name?.match(/(?<=\/files\/).*/g)}
                </a>
              ),
            },
            {
              title: t('category'),
              dataIndex: ['metadata', 'type'],
              key: '  ',
              render: (text) => text ?? 'Not Specified',
            },
            {
              title: t('uploadDate'),
              dataIndex: 'createdAt',
              key: 'uploadDate',
              render: (date) => date ? moment(date).format('DD/MM/YYYY HH:mm') : '',
            },
            {
              title: t('uploadedBy'),
              dataIndex: ['metadata', 'userid'],
              key: 'uploadedBy',
              render: (userid) => userid ?? t('unknownUser'),
            },
            {
              title: 'Processing Status',
              dataIndex: ['metadata', 'processing_status'],
              key: 'processing_status',
              render: (status) => status ?? 'N/A',
            },
            {
              title: '',
              key: 'actions',
              render: (_, record) => (
                <Space size="small">
                  {hasDocumentInfo(record?.name) && (
                    <Button
                      type="text"
                      icon={<FileTextOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        showMarkdownModal(record?.name);
                      }}
                      size="small"
                    />
                  )}
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => deleteItem({ file: record })}
                    size="small"
                    danger
                  />
                </Space>
              ),
            },
          ];

          return (
            <div key={groupName} style={{ marginBottom: '24px' }}>
              {groupName && (
                <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
                  {groupName}
                </h3>
              )}
              <Table
                columns={columns}
                dataSource={docs}
                rowKey="name"
                pagination={false}
                size="small"
                scroll={{ x: true }}
              />
            </div>
          );
        })}
      </div>

      <MarkdownModal
        open={markdownModalOpen}
        onClose={() => setMarkdownModalOpen(false)}
        fileName={selectedFileName}
        markdownContent={markdownContent}
        metadata={files.find(f => f.name.endsWith(selectedFileName))?.metadata}
        pdfUrl={files.find(f => f.name.endsWith(selectedFileName))?.url}
        assetId={id}
      />
    </div>
  );
};

export default UploadDocumentPage;
